/**
 * Shared Evaluation Utilities
 * 
 * These utilities provide a unified interface for evaluating exercise submissions
 * using the exercise registry.
 */

import { exerciseRegistry } from '../registry';
import type { 
  ExerciseDefinition, 
  ExerciseSubmissionPayload, 
  ExerciseEvaluationResult,
  ExerciseEvaluationDetail,
} from '../types';

// Import configs to ensure they're registered
import '../configs';

/**
 * Get the normalized max score for an exercise definition.
 */
function getNormalizedMaxScore(definition: ExerciseDefinition, fallback: number): number {
  const maxScore = definition.maxScore;
  if (typeof maxScore === 'number' && Number.isFinite(maxScore) && maxScore > 0) {
    return maxScore;
  }
  return fallback;
}

/**
 * Evaluate an exercise submission against its definition.
 * Returns null if the exercise type is not auto-gradable.
 */
export function evaluateExerciseSubmission(
  definition: ExerciseDefinition,
  submission: ExerciseSubmissionPayload
): ExerciseEvaluationResult | null {
  // Ensure type compatibility
  if (definition.type !== submission.type) {
    // Handle special case for Written exercises (LongAnswer/ShortAnswer)
    if (
      (definition.type === 'LongAnswer' || definition.type === 'ShortAnswer') &&
      (submission.type === 'LongAnswer' || submission.type === 'ShortAnswer')
    ) {
      // These types are not auto-gradable anyway
      return null;
    }
    return null;
  }

  const config = exerciseRegistry.get(definition.type);
  if (!config || !config.evaluate) {
    return null;
  }

  return config.evaluate(definition, submission);
}

/**
 * Get the default max score for an exercise definition.
 * Uses the registry to get type-specific defaults.
 */
export function getDefaultMaxScore(definition: ExerciseDefinition): number {
  const config = exerciseRegistry.get(definition.type);
  if (!config) {
    return getNormalizedMaxScore(definition, 100);
  }
  return config.defaultMaxScore(definition);
}

/**
 * Build an auto-evaluation summary from an evaluation result.
 */
export function buildAutoEvaluationSummary(
  result: ExerciseEvaluationResult | null
): Record<string, unknown> | undefined {
  if (!result) {
    return undefined;
  }

  return {
    score: result.score,
    maxScore: result.maxScore,
    isFullyCorrect: result.isFullyCorrect,
    details: result.details,
  } satisfies {
    score: number;
    maxScore: number;
    isFullyCorrect: boolean;
    details: ExerciseEvaluationDetail;
  };
}

/**
 * Check if an exercise type is auto-gradable.
 */
export function isAutoGradable(type: string): boolean {
  return exerciseRegistry.isAutoGradable(type);
}

/**
 * Get all auto-gradable exercise types.
 */
export function getAutoGradableTypes(): string[] {
  return exerciseRegistry.getAutoGradableTypes();
}
